import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { useUnsavedChangesTracking, useUnsavedChangesConfirmation } from '@hooks-clean/index';
import { useTranslation } from '@hooks/index';
import SubjectObservationsApi from '@apis/bff-api/subject-monitoring/subject-observations';
import SubjectReviewLogsApi from '@apis/bff-api/subject-monitoring/subject-review-log';

interface Props {
    studyRef: string;
    observationId: string;
    onSubmit: (id: number, addAndReview?: boolean) => void;
    onClose: () => void;
}

export interface AddReviewLog {
    dataset: { id: number; name: string };
    addAndReview: boolean;
}

const COMPONENT_NAME = 'add-subject-review-log-dialog';

const AddSubjectReviewLogDialog = ({ onClose, onSubmit, studyRef, observationId }: Props) => {
    const { t } = useTranslation('sm');

    const { data: datasetsData, isLoading: loadingDatasets } = SubjectObservationsApi.useGetDatasets(
        studyRef,
        observationId,
    );
    const { trigger: saveReviewLog } = SubjectReviewLogsApi.useSave(studyRef);
    const datasets = useMemo(
        () => datasetsData?.map((data) => ({ name: data?.datasetDate ?? '', id: data?.id ?? 0 })) ?? [],
        [datasetsData],
    );

    const onAddReviewLog = async (data: AddReviewLog) => {
        try {
            const response = await saveReviewLog({
                observationId: Number(observationId),
                datasetId: data.dataset.id,
            });

            if (response?.success) {
                return response.id!;
            }
            return 0;
        } catch {
            return 0;
        }
    };

    const dialogTitle = `${t('common:add')} ${t('reviewLogDialog.subjectTitle')}`;
    const primaryBtnLabel = `${t('common:add')}`;
    const secondaryBtnLabel = `${t('common:cancel')}`;
    const thirdButton = `${t('reviewLogDialog.addAndReview')}`;

    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<AddReviewLog>({
        mode: 'all',
        defaultValues: {
            dataset: undefined,
        },
    });

    useUnsavedChangesTracking({
        area: 'subject-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'subject-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    const submitAndClose = async (formData: AddReviewLog, addAndReview?: boolean) => {
        const id = await onAddReviewLog(formData);
        onSubmit(id, addAndReview);
    };

    const validatePanelHasData = (value: { id: number; name: string } | null) => {
        const validationError = t('reviewLogDialog.subjectDatasetValidation');

        if (value == null) return true;
        if (datasetsData && datasetsData.length > 0) {
            const matchingDataset = datasetsData.find((d) => d.id === value.id);
            return matchingDataset?.hasPanelData === false ? validationError : true;
        }

        return true;
    };

    const handlePrimarySubmit = () => handleSubmit((data) => submitAndClose(data, false))();

    const handleAdditionalSubmit = () => handleSubmit((data) => submitAndClose(data, true))();

    return (
        <DraggableDialog
            dataTestId="add-subject-review-log-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={handlePrimarySubmit}
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            secondaryBtnDisabled={isSubmitting}
            createdUpdatedBackgroundColour="gray"
            initialHeight={138}
            initialWidth={294}
            bodyPadding="none"
            setHeight="100%"
            secondaryBtnLabel={secondaryBtnLabel}
            additionalBtnDisabled={!isValid || !isDirty || isSubmitting}
            additionalBtnLabel={thirdButton}
            onAdditionalAction={handleAdditionalSubmit}
        >
            <form id="edit-subject-review-log" className="k-w-full k-pt-sm">
                <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-md k-pl-md k-pr-md">
                    <BasicDropDownController
                        label={t('reviewLogDialog.dataset')}
                        control={control}
                        name="dataset"
                        data-testid="add-review-log-dataset"
                        data={datasets}
                        textField="name"
                        dataItemKey="id"
                        filterable
                        loading={loadingDatasets}
                        allowClear
                        required
                        validateRules={{
                            checkHasPanelData: (value) => validatePanelHasData(value),
                        }}
                        readOnly={isSubmitting}
                    />
                </div>
            </form>
        </DraggableDialog>
    );
};

export default AddSubjectReviewLogDialog;
